export type ExperienceAtfCardAction = { type: 'link'; href: string } | { type: 'video'; videoId: string };

/** Matches ATF promo templates in Figma (node 7374:9689 + 7633:25654 + 7665:32868). */
export type ExperienceAtfPromoVariant = 'agentic_ai' | 'apr_2026_flash' | 'free' | 'marty_cagan_webinar' | 'mba';

export type ExperienceAtfCardConfig = {
  id: string;
  action: ExperienceAtfCardAction;
  ctaLabel: string;
  description: string;
  eyebrow: string;
  promoVariant: ExperienceAtfPromoVariant;
  /** Primary heading (H2 / tests). */
  title: string;
  /** Optional second line (Agentic card). */
  titleLine2?: string;
};

/** Static art for ATF promos (`public/images/experiments/ab_experience_atf_2026/`). */
export const ATF_PROMO_ASSETS = {
  flashPromoBg: '/images/experiments/ab_experience_atf_2026/48hr-flash-promo-bg.png',
  agenticAiBg: '/images/experiments/ab_experience_atf_2026/agentic-ai-bg-v2.png',
  martyCaganPortrait: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-portrait.png',
  martyCaganWaveBg: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-wave-bg.png',
  mbaBg: '/images/experiments/ab_experience_atf_2026/mba-bg.png',
  schoolsGrid: '/images/experiments/ab_experience_atf_2026/schools-grid.png',
} as const;

/**
 * Intrinsic pixel size of each file (must match what is on disk).
 * MBA art is exported @2x for a 300×375 CSS card; schools grid is still @1x until re-exported.
 * Flash promo bg is also @2x (600×750 for a 300×375 shell).
 */
export const ATF_PROMO_ASSET_INTRINSIC_PX = {
  flashPromoBg: { height: 750, width: 600 },
  martyCaganPortrait: { height: 280, width: 232 },
  martyCaganWaveBg: { height: 750, width: 600 },
  mbaBg: { height: 750, width: 600 },
  schoolsGrid: { height: 158, width: 205 },
} as const;

/** Desktop promo shell (Figma 7374:9689). */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX = 300;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX = 375;

/**
 * Mobile promo shell (Figma 7382:11004 — node 7382:11940 frame).
 * Outer slide width keeps a 5px inset each side, same as desktop.
 */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE = 260;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX_MOBILE = 325;

/** Swiper slide / static SSR row cell width (px); matches card outer width. */
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX = 310;
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE = 270;

/**
 * Min height for the carousel row (avoids CLS). Shell height + vertical inset (desktop 375+13, mobile 325+13).
 */
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX = 388;
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE = 338;

/**
 * Promo order (agentic → free → mba → marty_cagan_webinar). Agentic leads by default after
 * the free-trial experiment ended.
 */
export const EXPERIENCE_ATF_CARDS: ExperienceAtfCardConfig[] = [
  {
    /**
     * Default-path Agentic AI promo. After the `agentic-ai-ft-2026` free-trial experiment
     * ended, the free-trial visual shell was promoted as the OG `agentic` look (palette,
     * decoration, rocket CTA chrome); flag-on traffic still prepends `FREE_TRIAL_ATF_CARD`
     * but reuses the same OG variant + `ATF_agentic` analytics.
     */
    id: 'agentic_ai',
    promoVariant: 'agentic_ai',
    eyebrow: 'Most popular',
    title: 'Build AI systems',
    titleLine2: 'that act.',
    description: 'Every agentic AI skill, in one place.',
    ctaLabel: 'Enroll Now',
    action: { type: 'link', href: '/course/agentic-ai--nd900' },
  },
  {
    id: 'free_courses',
    promoVariant: 'free',
    eyebrow: 'Free programs',
    title: 'Start building AI skills for free',
    description: 'Explore top-rated courses and start today.',
    ctaLabel: 'Start Free Today',
    action: { type: 'link', href: '/catalog?price=free' },
  },
  {
    id: 'mba_ai',
    promoVariant: 'mba',
    eyebrow: 'New at Udacity',
    title: 'Level up with an MBA',
    description: 'Earn a real MBA credential designed for AI product leaders.',
    ctaLabel: 'Explore MBA in AI',
    action: { type: 'link', href: '/mba-ai-product-management' },
  },
  {
    id: 'marty_cagan_webinar_apr2026',
    promoVariant: 'marty_cagan_webinar',
    eyebrow: 'webinar',
    title: 'The new standard for PMs with Marty Cagan',
    description: 'Build, lead, and grow products in the age of AI',
    ctaLabel: 'Watch Now',
    action: { type: 'link', href: '/video/new-standard-for-product-managers-replay' },
  },
];

/**
 * First-position promo tile shown on the `agentic-ai-ft-2026=on` flag-on /experience page
 * (Figma 115:22190). Shares the OG `agentic` visual + analytics now that the free-trial
 * design was promoted to the default Agentic tile.
 */
export const FREE_TRIAL_ATF_CARD: ExperienceAtfCardConfig = {
  id: 'agentic_ai_free_trial',
  promoVariant: 'agentic_ai',
  eyebrow: 'Most popular',
  title: 'Build AI systems',
  titleLine2: 'that act.',
  description: 'Every agentic AI skill, in one place.',
  ctaLabel: 'Try For Free',
  action: {
    type: 'link',
    href: '/course/agentic-ai--nd900',
  },
};

export const EXPERIMENT_KEY = 'ab_experience_atf_2026';

/** Sanity `promotionId` that gates the flash-sale promo tile on /experience ATF. */
export const EXPERIENCE_ATF_PROMOTION_ID = 'april-48hrs';

/** First-position promo tile shown when the Sanity `april-48hrs` promotion is active (Figma 7831:7417). */
export const EXPERIENCE_ATF_FLASH_PROMO_CARD: ExperienceAtfCardConfig = {
  id: 'apr_2026_flash',
  promoVariant: 'apr_2026_flash',
  eyebrow: 'Flash Sale',
  title: '48% off, for 48 hrs.',
  description: "Don't wait to invest in new AI and tech skills. Countdown is on.",
  ctaLabel: 'Save Now',
  action: {
    type: 'link',
    href: '/quick-checkout?checkoutType=individual&billingFrequency=4month&checkoutPaymentType=credit-card',
  },
};
